import {
  NOTIFICATIONS_FAVICON_BADGE_CORNER_OVERFLOW_PX,
  NOTIFICATIONS_FAVICON_BADGE_FILL_COLOR,
  NOTIFICATIONS_FAVICON_BADGE_FONT,
  NOTIFICATIONS_FAVICON_BADGE_HEIGHT,
  NOTIFICATIONS_FAVICON_BADGE_PADDING_X,
  NOTIFICATIONS_FAVICON_BADGE_TEXT_COLOR,
  NOTIFICATIONS_FAVICON_BRAND_INSET_PX,
  NOTIFICATIONS_FAVICON_CANVAS_SIZE,
  NOTIFICATIONS_UNREAD_COUNT_DISPLAY_THRESHOLD,
} from "@frontend-shared/constants";
import { useEffect, useRef } from "react";

import { useUnreadCount } from "@/api/notifications/useUnreadCount";
import { useTheme } from "@/components/ui/theme-provider";
import { getDeclaredFaviconHref, setBadgeFaviconHref } from "@/lib/favicon";

import type {
  IDrawBadgedFaviconParams,
  IDrawPillParams,
} from "./use-favicon-badge.types";

// NOTE: Canvas roundRect is supported in every evergreen browser since
// Chrome 99 / Safari 16. We don't ship a polyfill because the favicon
// hook is web-only and we don't target legacy browsers.
const drawPill = ({ ctx, x, y, width, height }: IDrawPillParams): void => {
  const radius = height / 2;
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
};

const drawBadgedFavicon = ({
  baseImage,
  count,
}: IDrawBadgedFaviconParams): string | null => {
  const canvas = document.createElement("canvas");
  canvas.width = NOTIFICATIONS_FAVICON_CANVAS_SIZE;
  canvas.height = NOTIFICATIONS_FAVICON_CANVAS_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // NOTE: Paint the brand icon shrunk + anchored to bottom-left, leaving
  // an INSET-sized clear strip at the top + right where the pill badge
  // can sit without overlapping the logo silhouette. Without this the
  // badge looked "sunk into" the icon — and pushing the badge ABOVE the
  // canvas to compensate gets it clipped by the favicon's bounding box.
  // If the brand image is missing (load failure), the canvas stays
  // transparent and the pill alone becomes the favicon.
  if (baseImage) {
    const brandSize =
      NOTIFICATIONS_FAVICON_CANVAS_SIZE - NOTIFICATIONS_FAVICON_BRAND_INSET_PX;
    ctx.drawImage(
      baseImage,
      0,
      NOTIFICATIONS_FAVICON_BRAND_INSET_PX,
      brandSize,
      brandSize,
    );
  }

  const display =
    count > NOTIFICATIONS_UNREAD_COUNT_DISPLAY_THRESHOLD
      ? `${NOTIFICATIONS_UNREAD_COUNT_DISPLAY_THRESHOLD}+`
      : String(count);

  // NOTE: Pill width grows with text — "1" stays close to a circle,
  // "99+" stretches into a wider pill. Floor to BADGE_HEIGHT so single
  // digits don't collapse below the circle diameter.
  ctx.font = NOTIFICATIONS_FAVICON_BADGE_FONT;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const textWidth = ctx.measureText(display).width;
  const pillWidth = Math.max(
    NOTIFICATIONS_FAVICON_BADGE_HEIGHT,
    Math.ceil(textWidth) + NOTIFICATIONS_FAVICON_BADGE_PADDING_X * 2,
  );
  const pillHeight = NOTIFICATIONS_FAVICON_BADGE_HEIGHT;

  // NOTE: Anchor the pill flush with the canvas top-right corner. The
  // brand inset above already cleared an INSET-sized strip here so the
  // pill sits on transparent canvas, not on top of the logo — no white
  // outline is needed for legibility.
  const pillX =
    NOTIFICATIONS_FAVICON_CANVAS_SIZE -
    pillWidth +
    NOTIFICATIONS_FAVICON_BADGE_CORNER_OVERFLOW_PX;
  const pillY = -NOTIFICATIONS_FAVICON_BADGE_CORNER_OVERFLOW_PX;

  ctx.fillStyle = NOTIFICATIONS_FAVICON_BADGE_FILL_COLOR;
  drawPill({ ctx, x: pillX, y: pillY, width: pillWidth, height: pillHeight });
  ctx.fill();

  // NOTE: Text rendered LAST so it stacks on top of fill + stroke.
  // Centered on the pill's geometric centre.
  ctx.fillStyle = NOTIFICATIONS_FAVICON_BADGE_TEXT_COLOR;
  ctx.fillText(display, pillX + pillWidth / 2, pillY + pillHeight / 2);

  return canvas.toDataURL("image/png");
};

// NOTE: Paints the unread count as a small red pill in the top-right corner
// of the browser favicon — same surface every pinned-tab UI uses (Gmail,
// Slack, X). Reads from the React Query cache via `useUnreadCount({
// enabled: false })` so we don't trigger an extra fetch; UserMenu owns the
// authoritative request, this hook just subscribes for re-renders.
//
// NSAI-6321 — the hook no longer touches <link rel="icon"> itself. It writes
// the badge slot in `lib/favicon`, which renders the one icon the badge and the
// organization's custom favicon together imply; clearing the slot restores
// whatever that module holds underneath. That is also why the brand image is
// re-read whenever `customFaviconUrl` changes: the badge is painted ON TOP of
// the organization's own favicon, so a white-label tab shows the count over its
// own mark rather than over the platform's.
export const useFaviconBadge = (): void => {
  const { data } = useUnreadCount({ enabled: false });
  const count = data?.count ?? 0;
  const { customFaviconUrl } = useTheme();

  const baseImageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    // NOTE: Resolved per run rather than cached at mount: the organization's
    // favicon lands after the branding lookup answers, which is well after this
    // hook first mounts, and a href captured at mount would pin the badge to
    // the platform default for the rest of the session.
    //
    // Taken from the theme context rather than from `lib/favicon`'s own state:
    // this effect and the provider's both run when the organization changes,
    // and React runs this one first, so the module would still be holding the
    // previous organization's href. `customFaviconUrl` is a rendered value, so
    // it is already the new one here.
    const brandHref = customFaviconUrl ?? getDeclaredFaviconHref();

    if (count <= 0) {
      setBadgeFaviconHref(null);
      return undefined;
    }

    // NOTE: Per-effect-run staleness flag. The Image load is async; if
    // the count changes (or the hook unmounts) before `onload` fires,
    // the cleanup flips this to true and the late callback bails out
    // instead of repainting an outdated badge over the now-current
    // state (e.g. mark-all-read drops count → 0, but a pending paint
    // with the old count would otherwise reappear).
    let isStale = false;

    // NOTE: Cache the loaded brand-image element across re-renders so we
    // don't re-fetch the favicon for every count change. The Image load
    // is async; on the first run we paint after onload, every subsequent
    // run paints synchronously from the cached element. `baseImage` is
    // null when the load failed — `drawBadgedFavicon` handles that by
    // painting the pill alone on a transparent canvas.
    const paintWith = (baseImage: HTMLImageElement | null): void => {
      if (isStale) return;
      const dataUrl = drawBadgedFavicon({ baseImage, count });
      if (dataUrl) {
        setBadgeFaviconHref(dataUrl);
      }
    };

    // NOTE: The cache is keyed on the href it was loaded from, so a favicon
    // that changed under us (a save in Appearance, or the branding lookup
    // landing) is re-fetched instead of the badge being redrawn over the
    // previous organization's mark.
    const cached =
      baseImageRef.current?.src === new URL(brandHref, document.baseURI).href
        ? baseImageRef.current
        : null;
    if (cached?.complete && cached.naturalWidth > 0) {
      paintWith(cached);
      return (): void => {
        isStale = true;
      };
    }

    // NOTE: No `crossOrigin = "anonymous"` — the favicon is served from
    // the same origin as the app (Vite in dev, the API host in prod), so
    // setting crossOrigin would force a CORS preflight that fails when
    // the static asset response lacks `Access-Control-Allow-Origin`. The
    // canvas stays clean for same-origin images and `toDataURL` works.
    const baseImage = new Image();
    baseImage.onload = (): void => {
      baseImageRef.current = baseImage;
      paintWith(baseImage);
    };
    baseImage.onerror = (): void => {
      // NOTE: Favicon load can fail under strict CSP, a 404, or a
      // browser refusing the format via the Image API (Playwright's
      // Chromium silently rejects .ico, for one). Paint the pill
      // anyway on a transparent canvas — the user still gets the
      // unread indicator in the tab, just without the brand underneath.
      baseImageRef.current = null;
      paintWith(null);
    };
    baseImage.src = brandHref;

    return (): void => {
      isStale = true;
    };
  }, [count, customFaviconUrl]);

  // NOTE: Unmount-only restoration. AppLayout can unmount on logout or
  // navigation outside the protected layout — without this effect the
  // last badged data-URL favicon stays in the tab, so the next
  // unauthenticated page (or signed-out session) would still show the
  // stale unread count until something else rewrites the icon. Empty
  // deps so the cleanup only fires when the hook itself goes away.
  // Clearing the slot rather than restoring an href: the organization's own
  // favicon may still apply on the signed-out screens, and `lib/favicon` is
  // what knows that.
  useEffect(() => {
    return (): void => {
      setBadgeFaviconHref(null);
    };
  }, []);
};
